import isPlainObject from 'lodash/isPlainObject';

import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { logError } from 'utils/log';
import { createReducer } from 'utils/redux';
import { findAndRemoveItem } from 'utils/list';
import { t } from 'utils/language';
import { API, get, put } from 'utils/api';
import {
  addSelectedStatusToLookItems,
  compareByIds,
  getNewEmptyBucket,
  getUnsortedAttachmentsBucket,
  processData,
  resetApprovalStatus,
  setSelectedStatusForUnsorted,
  toggleSelectedItemStatus
} from 'utils/contentApproval';

import type { Action, ClientApprovalBucket, ContentApprovalGlobalState, ContentApprovalState, Dispatch, GetState, ThunkAction } from 'types';

const RESET = 'content-approval/RESET';
const MARK_CONTENT_APPROVAL_OPEN = 'content-approval/MARK_CONTENT_APPROVAL_OPEN';
const SET_LOADING_STATUS = 'content-approval/SET_LOADING_STATUS';
const SET_SAVE_IN_PROGRESS_STATUS = 'content-approval/SET_SAVE_IN_PROGRESS_STATUS';
const SAVING_FAILED = 'content-approval/SAVING_FAILED';
const LOADED = 'content-approval/LOADED';
const LOADING_FAILED = 'content-approval/LOADING_FAILED';
const ADD_BUCKET = 'content-approval/ADD_BUCKET';
const DELETE_BUCKET = 'content-approval/DELETE_BUCKET';
const EDIT_NOTE = 'content-approval/EDIT_NOTE';
const TOGGLE_SELECT_ITEM = 'content-approval/TOGGLE_SELECT_ITEM';
const SELECT_ALL_ITEMS = 'content-approval/SELECT_ALL_ITEMS';
const DESELECT_ALL_ITEMS = 'content-approval/DESELECT_ALL_ITEMS';
const DELETE_ITEM = 'content-approval/DELETE_ITEM';
const ASSIGN_ITEMS_TO_BUCKET = 'content-approval/ASSIGN_ITEMS_TO_BUCKET';
const UPDATE_LOOK_TITLE = 'content-approval/UPDATE_LOOK_TITLE';

export const markContentApprovalOpen = (deliverableId: number): Action => ({
  type: MARK_CONTENT_APPROVAL_OPEN,
  payload: { deliverableId }
});

export const resetContentApprovalModal = (deliverableId: number): Action => ({
  type: RESET,
  payload: { deliverableId }
});

export const addNewBucket = (title: string, deliverableId: number): Action => ({
  type: ADD_BUCKET,

  payload: {
    title,
    deliverableId
  }
});

export const deleteBucket = (bucketId: number, deliverableId: number): Action => ({
  type: DELETE_BUCKET,

  payload: {
    bucketId,
    deliverableId
  }
});

export const editNote = (bucketId: number, newNote: string, deliverableId: number): Action => ({
  type: EDIT_NOTE,

  payload: {
    bucketId,
    newNote,
    deliverableId
  }
});

export const toggleSelectItem = (selectedIndex: number, deliverableId: number): Action => ({
  type: TOGGLE_SELECT_ITEM,

  payload: {
    selectedIndex,
    deliverableId
  }
});

export const selectAllUnsortedItems = (deliverableId: number): Action => ({
  type: SELECT_ALL_ITEMS,

  payload: {
    deliverableId
  }
});

export const deselectAllUnsortedItems = (deliverableId: number): Action => ({
  type: DESELECT_ALL_ITEMS,

  payload: {
    deliverableId
  }
});

export const deleteItem = (bucketId: number, itemId: number, deliverableId: number): Action => ({
  type: DELETE_ITEM,

  payload: {
    bucketId,
    itemId,
    deliverableId
  }
});

const assignBucketItems = (bucketId: number, deliverableId: number): Action => ({
  type: ASSIGN_ITEMS_TO_BUCKET,

  payload: {
    bucketId,
    deliverableId
  }
});

export const updateLookTitle = (bucketId: number, title: string, deliverableId: number): Action => ({
  type: UPDATE_LOOK_TITLE,

  payload: {
    bucketId,
    deliverableId,
    title
  }
});

const setLoadingStatus = (isLoading: boolean, deliverableId: number): Action => ({
  type: SET_LOADING_STATUS,

  payload: {
    isLoading,
    deliverableId
  }
});

const bucketsLoaded = (buckets: Array<ClientApprovalBucket>, deliverableId: number): Action => ({
  type: LOADED,

  payload: {
    buckets,
    deliverableId
  }
});

const bucketsLoadingFailed = (deliverableId: number): Action => ({
  type: LOADING_FAILED,
  payload: { deliverableId }
});

const setSaveInProgressStatus = (isSaving: boolean, deliverableId: number): Action => ({
  type: SET_SAVE_IN_PROGRESS_STATUS,

  payload: {
    isSaving,
    deliverableId
  }
});

const bucketsSavingFailed = (hasSavingError: boolean, deliverableId: number): Action => ({
  type: SAVING_FAILED,

  payload: {
    hasSavingError,
    deliverableId
  }
});

export const fetchContentApprovalData = (campaignId: number, deliverableId: number, sort: string): ThunkAction => {
  const bucketsUrl = API.END_POINTS.DELIVERABLE.contentApprovalBuckets(campaignId, deliverableId, sort);
  const attachmentsUrl = API.END_POINTS.DELIVERABLE.getDeliverableAttachments(campaignId, deliverableId);

  return async (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true, deliverableId));

    return Promise.all([get(attachmentsUrl), get(bucketsUrl)])
      .then(([attachmentsResponse, bucketsResponse]) => {
        const buckets = addSelectedStatusToLookItems(bucketsResponse.data.buckets);
        let allAttachments = attachmentsResponse.data;

        // MediaPreview needs `format` which needs `assetType`. So add it here.
        allAttachments = allAttachments.map(attachment => ({
          ...attachment,
          assetType: attachment.mediaType
        }));

        const unsortedAttachmentsBucket = getUnsortedAttachmentsBucket(allAttachments, buckets);
        const bucketsToAdd = buckets.length ? buckets : [getNewEmptyBucket(t('content_approval.buckets.looks.default_title', { index: 1 }))];
        dispatch(bucketsLoaded([unsortedAttachmentsBucket, ...bucketsToAdd], deliverableId));
      })
      .catch((e) => {
        logError(e);
        dispatch(danger(t('content_approval.loading_error')));
        dispatch(bucketsLoadingFailed(deliverableId));
      });
  };
};

export const saveContentApprovalData = (campaignId: number, deliverableId: number): ThunkAction => {
  const bucketsUrl = API.END_POINTS.DELIVERABLE.contentApprovalBuckets(campaignId, deliverableId);

  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch(setSaveInProgressStatus(true, deliverableId));
    return put(bucketsUrl, {
      data: {
        buckets: processData(getState().contentApproval.deliverables[deliverableId].buckets)
      }
    })
      .catch((e) => {
        logError(e);
        dispatch(danger(t('content_approval.saving_error')));
        dispatch(bucketsSavingFailed(true, deliverableId));
      })
      .finally(() => dispatch(setSaveInProgressStatus(false, deliverableId)));
  };
};

export function confirmDeleteBucket(bucketId: number, deliverableId: number): ThunkAction {
  return async (dispatch) => {
    dispatch(showConfirmModal({
      title: t('content_approval.modal_delete.title'),
      message: t('content_approval.modal_delete.message'),
      confirmButtonLabel: t('content_approval.modal_delete.button'),
      onConfirm: () => {
        dispatch(deleteBucket(bucketId, deliverableId));
        dispatch(hideConfirmModal());
      },
      onCancel: () => dispatch(hideConfirmModal())
    }));
  };
}

export function confirmUnsavedClose(): ThunkAction {
  return async (dispatch) => new Promise<void>((resolve) => {
    dispatch(showConfirmModal({
      title: t('content_approval.prompt_exit.title'),
      message: t('content_approval.prompt_exit.message'),
      confirmButtonLabel: t('content_approval.prompt_exit.button'),
      onConfirm: () => {
        dispatch(hideConfirmModal());
        resolve();
      },
      onCancel: () => dispatch(hideConfirmModal())
    }));
  });
}

export const addBucket = (title: string, deliverableId: number): ThunkAction => async (dispatch: Dispatch) => {
  try {
    dispatch(addNewBucket(title, deliverableId));
  } catch (e) {
    logError(e);
  }
};

export const assignItemsToBucket = (bucketId: number, deliverableId: number): ThunkAction => async (dispatch: Dispatch) => {
  try {
    dispatch(assignBucketItems(bucketId, deliverableId));
  } catch (e) {
    logError(e);
  }
};

const initialDeliverableSubState: ContentApprovalState = {
  buckets: [],
  isOpen: false,
  isLoading: false,
  hasError: false
};

const initialState: ContentApprovalGlobalState = {
  deliverables: {}
};

function updatePropInBucket(state, bucketId, prop, value) {
  return state.buckets.map(bucket => {
    if (bucket.id === bucketId) {
      const newValue = isPlainObject(bucket[prop]) ? { ...bucket[prop], ...value } : value;
      return {
        ...bucket,
        // $FlowIgnore - Cannot use `prop` as a computed property. Computed properties may only be primitive literal values
        [prop]: newValue
      };
    }
    return bucket;
  });
}

function getDeliverableState(state, deliverableId) {
  if (state.deliverables[deliverableId]) {
    return state.deliverables[deliverableId];
  }

  return initialDeliverableSubState;
}

function updateDeliverableState(state, deliverableId, stateDiff) {
  const newState = {
    deliverables: {
      ...state.deliverables
    }
  };
  if (!newState.deliverables[deliverableId]) {
    newState.deliverables[deliverableId] = {
      ...initialDeliverableSubState
    };
  }

  // $FlowIgnore
  newState.deliverables[deliverableId] = {
    ...newState.deliverables[deliverableId],
    ...stateDiff
  };

  return newState;
}

export default createReducer<ContentApprovalGlobalState, any>('contentApproval', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: (state, { deliverableId }) =>
    updateDeliverableState(state, deliverableId, initialDeliverableSubState),
  [MARK_CONTENT_APPROVAL_OPEN]: (state, { deliverableId }) =>
    updateDeliverableState(state, deliverableId, { isOpen: true }),
  [LOADED]: (state, { buckets, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      isLoading: false,
      buckets
    }),
  [SET_LOADING_STATUS]: (state, { isLoading, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      isLoading
    }),
  [LOADING_FAILED]: (state, { deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      isLoading: false,
      hasError: true
    }),
  [SET_SAVE_IN_PROGRESS_STATUS]: (state, { isSaving, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      isSaving,
      hasSavingError: false
    }),
  [SAVING_FAILED]: (state, { hasSavingError, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      hasSavingError,
      isSaving: false
    }),
  [ADD_BUCKET]: (state, { title, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: getDeliverableState(state, deliverableId).buckets.concat(getNewEmptyBucket(title))
    }),
  [DELETE_BUCKET]: (state, { bucketId, deliverableId }) => {
    const currentBuckets = getDeliverableState(state, deliverableId).buckets;
    const bucketToRemove = currentBuckets.find(bucket => bucket.id === bucketId);
    const cardsToMove = bucketToRemove ? bucketToRemove.items.map(resetApprovalStatus) : [];
    const newUnsortedBucket = { ...currentBuckets[0], items: currentBuckets[0].items.concat(cardsToMove) };
    const newBuckets = [newUnsortedBucket, ...findAndRemoveItem(currentBuckets.slice(1), bucketToRemove, compareByIds)];
    return updateDeliverableState(state, deliverableId, {
      buckets: newBuckets
    });
  },
  [EDIT_NOTE]: (state, { bucketId, newNote, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: updatePropInBucket(
        getDeliverableState(state, deliverableId),
        bucketId, 'notes', { accountManager: newNote }
      )
    }),
  [TOGGLE_SELECT_ITEM]: (state, { selectedIndex, deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: toggleSelectedItemStatus(
        getDeliverableState(state, deliverableId).buckets,
        selectedIndex
      )
    }),
  [SELECT_ALL_ITEMS]: (state, { deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: setSelectedStatusForUnsorted(
        getDeliverableState(state, deliverableId).buckets,
        true
      )
    }),
  [DESELECT_ALL_ITEMS]: (state, { deliverableId }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: setSelectedStatusForUnsorted(
        getDeliverableState(state, deliverableId).buckets,
        false
      )
    }),
  [UPDATE_LOOK_TITLE]: (state, { bucketId, deliverableId, title }) =>
    updateDeliverableState(state, deliverableId, {
      buckets: updatePropInBucket(
        getDeliverableState(state, deliverableId),
        bucketId, 'title', title
      )
    }),
  [DELETE_ITEM]: (state, { bucketId, itemId, deliverableId }) => {
    const unsortedBucket = getDeliverableState(state, deliverableId).buckets[0];
    const sourceBucket = getDeliverableState(state, deliverableId)
      .buckets
      .find(bucket => bucket.id === bucketId);
    const removedItem = sourceBucket ? sourceBucket.items.find(item => item.id === itemId) : [];
    const remainingItems = sourceBucket ? sourceBucket.items.filter(item => item.id !== itemId) : [];

    const updatedBuckets = getDeliverableState(state, deliverableId)
      .buckets
      .map(bucket => {
        if (bucket.id === unsortedBucket.id) {
          return {
            ...bucket,
            items: [
              ...bucket.items,
              removedItem
            ]
          };
        }

        if (bucket.id === sourceBucket?.id) {
          return {
            ...bucket,
            items: remainingItems
          };
        }

        return bucket;
      });

    return updateDeliverableState(state, deliverableId, {
      buckets: updatedBuckets
    });
  },
  [ASSIGN_ITEMS_TO_BUCKET]: (state, { bucketId, deliverableId }) => {
    const sourceBucket = getDeliverableState(state, deliverableId).buckets[0];
    const destinationBucket = getDeliverableState(state, deliverableId)
      .buckets
      .find(bucket => bucket.id === bucketId);
    const selectedItems = sourceBucket.items.filter(item => item.isSelected);
    const unselectedItems = sourceBucket.items.filter(item => !item.isSelected);

    const updatedBuckets = getDeliverableState(state, deliverableId)
      .buckets
      .map(bucket => {
        if (bucket.id === sourceBucket.id) {
          return {
            ...bucket,
            items: unselectedItems
          };
        }

        if (bucket.id === destinationBucket?.id) {
          return {
            ...bucket,
            items: [
              ...(destinationBucket?.items || []),
              ...selectedItems
            ]
          };
        }

        return bucket;
      });

    return updateDeliverableState(state, deliverableId, {
      buckets: updatedBuckets
    });
  }
});
